import { Controller } from "@hotwired/stimulus"
import { draftKey, buildDraft, readDraft, isPristine, ageText, SAVE_DELAY } from "../modules/product_draft.mjs"
import { purgeUpload } from "../modules/direct_upload_purge.js"
import { firstIncompleteStep } from "../modules/product_wizard.mjs"

// Borrador local de Crear producto: guarda en localStorage lo que cada tarjeta serializa, ofrece o
// aplica el vigente al abrir `new` y lo borra al guardar. Doc: _docs/features/products/product_wizard_draft.md
const NAME = "input[name='product[name]']"
const DESCRIPTION = "input[name='product[description]']"

export default class extends Controller {
  static targets = ["notice", "age", "skipped"]
  static values = { store: Number, restore: Boolean, auto: Boolean }

  connect() {
    this.key = draftKey({ storeId: this.storeValue })
    // Un preview de Turbo nunca ofrece ni escribe; se decide al conectar
    this.preview = document.documentElement.hasAttribute("data-turbo-preview")
    this.pending = null   // borrador ofrecido, todavía sin decidir
    this.decided = false  // ya se decidió (o no había nada que ofrecer)
    this.done = false     // el producto se envió con éxito: no se escribe más
    if (this.preview) return
    this.onLeave = () => this.flush()
    // En el teléfono, pasar a segundo plano no siempre dispara pagehide
    this.onHide = () => { if (document.hidden) this.flush() }
    addEventListener("pagehide", this.onLeave)
    document.addEventListener("turbo:before-visit", this.onLeave)
    document.addEventListener("visibilitychange", this.onHide)
  }

  disconnect() {
    if (this.preview) return
    this.flush()
    removeEventListener("pagehide", this.onLeave)
    document.removeEventListener("turbo:before-visit", this.onLeave)
    document.removeEventListener("visibilitychange", this.onHide)
  }

  // product-wizard:state, una vez por cambio; el primero decide si se ofrece un borrador (solo sobre un form prístino)
  changed(event) {
    if (this.preview || this.applying || this.done) return
    this.state = event.detail?.state
    if (!this.decided && !this.pending) this.#decide()
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.flush(), SAVE_DELAY)
  }

  // Escribe ya (fin del debounce, salida, disconnect); un form prístino no se guarda y borra lo que esta página escribió
  flush() {
    clearTimeout(this.timer)
    this.timer = null
    if (this.preview || !this.state || this.applying || this.done) return
    const draft = this.#collect()
    if (isPristine(draft)) {
      if (this.decided && this.wrote) this.#remove()
      return
    }
    this.#write(draft)
  }

  continue() {
    const draft = this.pending
    this.pending = null
    this.decided = true
    this.noticeTarget.hidden = true
    if (draft) this.#apply(draft)
  }

  // Borra el borrador y purga su foto: era una subida sin guardar que ya nadie referencia
  discard() {
    const draft = this.pending
    this.pending = null
    this.decided = true
    this.noticeTarget.hidden = true
    this.#remove()
    const signedId = draft?.product?.photo?.signedId
    if (signedId) purgeUpload(signedId)
  }

  // turbo:submit-end con éxito: persiste lo pendiente y deja de escribir; el borrado lo hace Mis productos (flash product_saved)
  submitted(event) {
    if (!event.detail?.success) return
    this.flush()
    this.done = true
  }

  #decide() {
    const stored = this.restoreValue ? this.#read() : null
    if (stored && isPristine(this.#collect())) {
      if (this.autoValue) {
        this.decided = true
        this.#apply(stored)
        return
      }
      this.pending = stored
      this.ageTarget.textContent = ageText(stored.savedAt)
      this.noticeTarget.hidden = false
      return
    }
    this.decided = true
  }

  // Un solo JSON con lo que cada tarjeta sabe de sí misma; el paso se lee del atributo que publica product-wizard
  #collect() {
    const form = this.#form
    const card = identifier => this.#card(identifier)
    return buildDraft({
      storeId: this.storeValue,
      mode: "create",
      step: Number(this.element.dataset.productWizardStepValue) || 1,
      product: {
        name: form?.querySelector(NAME)?.value || "",
        sectionId: card("choice-sheet")?.draft || "",
        categoryIds: card("chip-limit")?.draft || [],
        description: form?.querySelector(DESCRIPTION)?.value || "",
        photo: card("photo-field")?.draft || null
      },
      price: {
        amount: card("money-input")?.draft || "",
        discount: card("discount-form")?.draft || null,
        stock: card("stock-field")?.draft || null
      },
      options: card("product-options")?.draft || null
    })
  }

  // Reparte el borrador a cada tarjeta, vuelve al paso guardado y persiste; lo no recuperado se cuenta y avisa
  #apply(draft) {
    this.applying = true
    const form = this.#form
    const name = form?.querySelector(NAME)
    if (name) name.value = draft.product?.name || ""
    this.#restoreDescription(form, draft.product?.description || "")
    let skipped = 0
    skipped += this.#restore("photo-field", draft.product?.photo)
    skipped += this.#restore("choice-sheet", draft.product?.sectionId)
    skipped += this.#restore("chip-limit", draft.product?.categoryIds)
    skipped += this.#restore("money-input", draft.price?.amount)
    skipped += this.#restore("discount-form", draft.price?.discount)
    skipped += this.#restore("stock-field", draft.price?.stock)
    skipped += this.#restore("product-options", draft.options)
    this.applying = false
    if (this.hasSkippedTarget) this.skippedTarget.hidden = skipped === 0
    const wizard = this.#wizard
    if (wizard) {
      const state = wizard.state()
      const incomplete = firstIncompleteStep(state)
      wizard.show(Math.min(Number(draft.step) || 1, incomplete || 4))
      this.state = state
    }
    this.flush()
  }

  #restore(identifier, data) {
    if (data === undefined || data === null || data === "") return 0
    const controller = this.#card(identifier)
    if (!controller?.restoreDraft) return 0
    return Number(controller.restoreDraft(data)?.skipped) || 0
  }

  // El hidden alimenta a Trix cuando todavía no inicializó; si ya está, loadHTML (sanitiza)
  #restoreDescription(form, html) {
    const input = form?.querySelector(DESCRIPTION)
    if (input) input.value = html
    const editor = form?.querySelector("trix-editor")
    if (editor?.editor) editor.editor.loadHTML(html)
  }

  get #form() {
    return this.element.querySelector("form")
  }

  get #wizard() {
    return this.application.getControllerForElementAndIdentifier(this.element, "product-wizard")
  }

  #card(identifier) {
    const element = this.#form?.querySelector(`[data-controller~='${identifier}']`)
    return element ? this.application.getControllerForElementAndIdentifier(element, identifier) : null
  }

  #read() {
    return readDraft(localStorage, this.key, { storeId: this.storeValue })
  }

  #write(draft) {
    try {
      localStorage.setItem(this.key, JSON.stringify(draft))
      this.wrote = true
    } catch { /* sin storage, sin borrador */ }
  }

  #remove() {
    try { localStorage.removeItem(this.key) } catch { /* sin storage no hay nada que borrar */ }
  }
}
